import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Camera,
  Trash2,
  Pencil,
  Play,
  Image as ImageIcon,
  RefreshCw,
  Folder,
  FolderPlus,
  ChevronRight,
  ChevronDown,
  FolderInput,
  HardDrive,
  ShieldCheck,
} from 'lucide-react'
import { useAppStore } from '@core/store'
import {
  applyLocalViewpoint,
  captureLocalViewpoint,
  updateLocalViewpoint,
} from '@viewer/lib/viewpointCapture'
import { viewpointsStore, type LocalViewpoint } from '@viewer/lib/viewpointsStore'
import { doModeloActivo, GRUPO_GRANDE, PASTA_IMPORTADOS } from './listaDeViewpoints'
import { pruneOrphanViewpoints } from '@core/session/closeSession'
import {
  estimateOriginStorage,
  formatBytes,
  isStoragePersistent,
  requestPersistent,
} from '@viewer/lib/storageInfo'

// Inner content for the viewpoints panel, used identically by the
// fixed sidebar, the detached floating window and the popout browser
// window. Self-contained: pulls list + actions from the slice and
// owns the capture-form local state.

const NEW_FOLDER_SENTINEL = '__new__'
const ROOT_LABEL = 'Sem pasta'

function ViewpointsPanel() {
  const list = useAppStore((s) => s.viewpoints)
  const loaded = useAppStore((s) => s.viewpointsLoaded)
  const loading = useAppStore((s) => s.viewpointsLoading)
  const refresh = useAppStore((s) => s.refreshViewpoints)
  const upsert = useAppStore((s) => s.upsertViewpointInList)
  const removeFromList = useAppStore((s) => s.removeViewpointFromList)
  const models = useAppStore((s) => s.models)
  const activeModelId = useAppStore((s) => s.activeModelId)
  const [filterToActive, setFilterToActive] = useState(true)
  const [name, setName] = useState('')
  const [folder, setFolder] = useState<string>('')
  const [capturing, setCapturing] = useState(false)
  const [captureError, setCaptureError] = useState<string | null>(null)
  const [applyError, setApplyError] = useState<string | null>(null)
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})

  // First-time load. Refresh on every model list change so a freshly
  // opened IFC immediately surfaces its associated viewpoints.
  useEffect(() => {
    if (!loaded) void refresh()
  }, [loaded, refresh])

  const activeHash = useMemo(() => {
    return (
      models.find((m) => m.id === activeModelId)?.sha256 ??
      models.find((m) => m.visible)?.sha256 ??
      models[0]?.sha256 ??
      null
    )
  }, [models, activeModelId])

  const visible = useMemo(() => {
    let out = list
    if (filterToActive && activeHash) {
      // Os importados de BCF não estão presos a um modelo (primaryHash
      // vazio): contam para qualquer modelo. Antes ficavam todos de fora
      // e o contador dizia 1191 com a lista vazia.
      out = out.filter((v) => doModeloActivo(v, activeHash))
    }
    return out
  }, [list, filterToActive, activeHash])

  const folderNames = useMemo(() => {
    const set = new Set<string>()
    for (const v of list) {
      if (v.folder) set.add(v.folder)
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt'))
  }, [list])

  // Group visible viewpoints by folder, root group first.
  const groups = useMemo(() => {
    const byFolder = new Map<string | null, LocalViewpoint[]>()
    for (const v of visible) {
      const key = v.folder ?? (v.primaryHash ? null : PASTA_IMPORTADOS)
      const arr = byFolder.get(key) ?? []
      arr.push(v)
      byFolder.set(key, arr)
    }
    const ordered: { key: string | null; label: string; items: LocalViewpoint[] }[] =
      []
    if (byFolder.has(null)) {
      ordered.push({
        key: null,
        label: ROOT_LABEL,
        items: byFolder.get(null)!,
      })
    }
    const folderKeys = Array.from(byFolder.keys()).filter(
      (k) => k !== null && k !== PASTA_IMPORTADOS,
    ) as string[]
    folderKeys.sort((a, b) => a.localeCompare(b, 'pt'))
    for (const k of folderKeys) {
      ordered.push({ key: k, label: k, items: byFolder.get(k)! })
    }
    if (byFolder.has(PASTA_IMPORTADOS)) {
      ordered.push({ key: PASTA_IMPORTADOS, label: 'Importados de BCF', items: byFolder.get(PASTA_IMPORTADOS)! })
    }
    return ordered
  }, [visible])

  const onCapture = async () => {
    setCaptureError(null)
    setCapturing(true)
    try {
      const vp = await captureLocalViewpoint({
        name,
        folder: folder || null,
      })
      upsert(vp)
      setName('')
    } catch (err) {
      setCaptureError(err instanceof Error ? err.message : String(err))
    } finally {
      setCapturing(false)
    }
  }

  const onApply = async (vp: LocalViewpoint) => {
    setApplyError(null)
    try {
      const { missing } = await applyLocalViewpoint(vp)
      if (missing.length > 0) {
        setApplyError(
          `Faltam ${missing.length} modelo(s) IFC para reconstruir totalmente este viewpoint. A vista de câmara foi aplicada.`,
        )
      }
    } catch (err) {
      setApplyError(err instanceof Error ? err.message : String(err))
    }
  }

  const onDelete = async (vp: LocalViewpoint) => {
    if (!window.confirm(`Apagar o viewpoint "${vp.name}"?`)) return
    await viewpointsStore.remove(vp.id)
    removeFromList(vp.id)
  }

  const onRename = async (vp: LocalViewpoint) => {
    const next = window.prompt('Novo nome', vp.name)?.trim()
    if (!next || next === vp.name) return
    const updated = await viewpointsStore.update(vp.id, { name: next })
    if (updated) upsert(updated)
  }

  const onUpdate = async (vp: LocalViewpoint) => {
    if (
      !window.confirm(
        `Substituir a vista guardada de "${vp.name}" pela câmara/estado atuais?`,
      )
    )
      return
    try {
      const next = await updateLocalViewpoint(vp.id)
      upsert(next)
    } catch (err) {
      setCaptureError(err instanceof Error ? err.message : String(err))
    }
  }

  const onMove = async (vp: LocalViewpoint) => {
    const choices = [...folderNames]
    let target: string | null
    if (choices.length === 0) {
      const v = window.prompt(
        'Mover para pasta (deixar vazio = sem pasta):',
        vp.folder ?? '',
      )
      if (v === null) return
      target = v.trim() || null
    } else {
      const lines = ['1) Sem pasta']
      choices.forEach((c, i) => lines.push(`${i + 2}) ${c}`))
      lines.push(`${choices.length + 2}) Nova pasta…`)
      const ans = window.prompt(
        `Pasta destino, escolhe um número:\n${lines.join('\n')}`,
        vp.folder ? String(choices.indexOf(vp.folder) + 2) : '1',
      )
      if (ans === null) return
      const n = Number(ans.trim())
      if (!Number.isFinite(n) || n < 1) return
      if (n === 1) target = null
      else if (n >= 2 && n <= choices.length + 1) target = choices[n - 2]
      else if (n === choices.length + 2) {
        const fresh = window.prompt('Nome da nova pasta:')?.trim()
        if (!fresh) return
        target = fresh
      } else return
    }
    const updated = await viewpointsStore.update(vp.id, { folder: target })
    if (updated) upsert(updated)
  }

  const onCreateFolder = () => {
    const fresh = window.prompt('Nome da nova pasta:')?.trim()
    if (!fresh) return
    setFolder(fresh)
  }

  const onRenameFolder = async (oldName: string) => {
    const fresh = window.prompt('Renomear pasta para:', oldName)?.trim()
    if (!fresh || fresh === oldName) return
    const affected = list.filter((v) => v.folder === oldName)
    for (const v of affected) {
      const next = await viewpointsStore.update(v.id, { folder: fresh })
      if (next) upsert(next)
    }
  }

  const onDeleteFolder = async (folderName: string) => {
    const affected = list.filter((v) => v.folder === folderName)
    if (
      !window.confirm(
        `Esvaziar a pasta "${folderName}" (${affected.length} viewpoint(s) movidos para sem pasta)?`,
      )
    )
      return
    for (const v of affected) {
      const next = await viewpointsStore.update(v.id, { folder: null })
      if (next) upsert(next)
    }
  }

  const toggleCollapse = (key: string) => {
    setCollapsed((c) => ({ ...c, [key]: !c[key] }))
  }

  const noModel = models.length === 0

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-panel)] p-3">
        <div className="mb-2 flex items-center gap-2">
          <input
            type="text"
            value={name}
            placeholder="Nome do viewpoint (opcional)"
            onChange={(e) => setName(e.target.value)}
            disabled={capturing || noModel}
            className="flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs text-[var(--color-text)] outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          />
          <button
            type="button"
            onClick={onCapture}
            disabled={capturing || noModel}
            title={
              noModel
                ? 'Carrega um IFC para capturar uma vista'
                : 'Capturar vista atual (câmara + secção + seleção + escondidos + transparências) com thumbnail'
            }
            className="flex shrink-0 items-center gap-1 rounded bg-[var(--color-accent)] px-2 py-1 text-xs font-medium text-[var(--color-bg)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Camera size={12} strokeWidth={2} />
            <span>{capturing ? 'A capturar…' : 'Capturar'}</span>
          </button>
        </div>
        <div className="mb-2 flex items-center gap-2">
          <Folder
            size={12}
            strokeWidth={1.6}
            className="text-[var(--color-text-muted)]"
          />
          <select
            value={folder ? (folderNames.includes(folder) ? folder : NEW_FOLDER_SENTINEL) : ''}
            onChange={(e) => {
              if (e.target.value === NEW_FOLDER_SENTINEL) onCreateFolder()
              else setFolder(e.target.value)
            }}
            disabled={capturing || noModel}
            className="flex-1 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-xs text-[var(--color-text)] outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          >
            <option value="">Sem pasta</option>
            {folderNames.map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
            {folder && !folderNames.includes(folder) && (
              <option value={folder}>{folder} (nova)</option>
            )}
            <option value={NEW_FOLDER_SENTINEL}>+ Nova pasta…</option>
          </select>
          <button
            type="button"
            onClick={onCreateFolder}
            disabled={capturing || noModel}
            title="Nova pasta"
            aria-label="Nova pasta"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            <FolderPlus size={12} strokeWidth={1.6} />
          </button>
        </div>
        {captureError && (
          <div className="mb-2 rounded border border-[var(--color-error-border)] bg-[var(--color-error-bg)] px-2 py-1 text-[11px] text-[var(--color-error-text)]">
            {captureError}
          </div>
        )}
        <label className="flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]">
          <input
            type="checkbox"
            checked={filterToActive}
            onChange={(e) => setFilterToActive(e.target.checked)}
            className="accent-[var(--color-accent)]"
          />
          Mostrar só viewpoints do modelo ativo
        </label>
        <button
          type="button"
          onClick={async () => {
            const removed = await pruneOrphanViewpoints()
            if (removed > 0) {
              alert(
                `${removed} viewpoint(s) não anexado(s) apagados. Os anexados a tópicos BCF mantiveram-se.`,
              )
            } else {
              alert('Nenhum viewpoint não anexado para apagar.')
            }
          }}
          title="Apaga viewpoints que não estão anexados a nenhum tópico BCF (capturas soltas). Os anexados ficam intactos."
          className="ml-auto rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
        >
          Limpar não anexados
        </button>
      </div>

      {applyError && (
        <div className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-warn-bg)] px-3 py-1.5 text-[11px] text-[var(--color-warn-text)]">
          {applyError}
          <button
            type="button"
            onClick={() => setApplyError(null)}
            className="ml-2 text-[10px] underline hover:no-underline"
          >
            Fechar
          </button>
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2">
        {loading && !loaded && (
          <div className="px-2 py-3 text-xs text-[var(--color-text-muted)]">
            A carregar viewpoints…
          </div>
        )}
        {loaded && visible.length === 0 && (
          <div className="px-2 py-3 text-xs italic text-[var(--color-text-dim)]">
            {noModel
              ? 'Sem modelos carregados.'
              : filterToActive
                ? 'Não há viewpoints para o modelo ativo. Captura um primeiro.'
                : 'Ainda não tens viewpoints guardados.'}
          </div>
        )}
        <StorageFooter list={list} />
        {groups.map((g) => {
          const collapseKey = g.key ?? '__root__'
          // Grupos grandes (os importados de um BCF de conflitos têm
          // centenas) abrem dobrados, para não desenhar tudo de uma vez.
          const isCollapsed = collapsed[collapseKey] ?? g.items.length > GRUPO_GRANDE
          return (
            <div key={collapseKey} className="mb-3">
              <div className="mb-1 flex items-center justify-between gap-1 rounded bg-[var(--color-bg)]/50 px-1.5 py-1">
                <button
                  type="button"
                  onClick={() => toggleCollapse(collapseKey)}
                  className="flex flex-1 items-center gap-1 text-[11px] font-medium text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
                  title={isCollapsed ? 'Expandir' : 'Colapsar'}
                >
                  {isCollapsed ? (
                    <ChevronRight size={12} strokeWidth={1.6} />
                  ) : (
                    <ChevronDown size={12} strokeWidth={1.6} />
                  )}
                  <Folder size={11} strokeWidth={1.6} />
                  <span className="truncate">{g.label}</span>
                  <span className="text-[10px] text-[var(--color-text-dim)]">
                    ({g.items.length})
                  </span>
                </button>
                {g.key !== null && g.key !== PASTA_IMPORTADOS && (
                  <div className="flex items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => void onRenameFolder(g.key as string)}
                      title="Renomear pasta"
                      aria-label="Renomear pasta"
                      className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
                    >
                      <Pencil size={10} strokeWidth={1.6} />
                    </button>
                    <button
                      type="button"
                      onClick={() => void onDeleteFolder(g.key as string)}
                      title="Esvaziar pasta (move viewpoints para sem pasta)"
                      aria-label="Esvaziar pasta"
                      className="flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-error-bg)] hover:text-[var(--color-error-text)]"
                    >
                      <Trash2 size={10} strokeWidth={1.6} />
                    </button>
                  </div>
                )}
              </div>
              {!isCollapsed && (
                <ul className="grid grid-cols-1 gap-2">
                  {g.items.map((vp) => (
                    <ViewpointCard
                      key={vp.id}
                      vp={vp}
                      onApply={() => void onApply(vp)}
                      onDelete={() => void onDelete(vp)}
                      onRename={() => void onRename(vp)}
                      onUpdate={() => void onUpdate(vp)}
                      onMove={() => void onMove(vp)}
                    />
                  ))}
                </ul>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function StorageFooter({ list }: { list: LocalViewpoint[] }) {
  const [usage, setUsage] = useState<number | null>(null)
  const [quota, setQuota] = useState<number | null>(null)
  const [persistent, setPersistent] = useState<boolean>(false)
  const [thumbBytes, setThumbBytes] = useState<number>(0)
  const [metaBytes, setMetaBytes] = useState<number>(0)
  const [requesting, setRequesting] = useState(false)

  const refreshAll = useCallback(async () => {
    const [origin, isPersist, breakdown] = await Promise.all([
      estimateOriginStorage(),
      isStoragePersistent(),
      viewpointsStore.sizeBreakdown(),
    ])
    setUsage(origin.usageBytes)
    setQuota(origin.quotaBytes)
    setPersistent(isPersist)
    setThumbBytes(breakdown.thumbnailBytes)
    setMetaBytes(breakdown.metaBytes)
  }, [])

  // Recompute whenever the viewpoint list changes (capture, update,
  // delete) so the user sees usage move in real-time. Debounced
  // implicitly by React state batching.
  useEffect(() => {
    // A regra não vê para lá do `await`: o `refreshAll` só escreve
    // estado depois do `await Promise.all(...)`, por isso não há render
    // em cascata para evitar aqui.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refreshAll()
  }, [list, refreshAll])

  const onAskPersistent = async () => {
    setRequesting(true)
    try {
      const ok = await requestPersistent()
      setPersistent(ok)
    } finally {
      setRequesting(false)
    }
  }

  const pct =
    usage !== null && quota !== null && quota > 0
      ? Math.min(100, (usage / quota) * 100)
      : null
  const ownBytes = thumbBytes + metaBytes
  const barColor =
    pct === null
      ? 'var(--color-text-dim)'
      : pct > 90
        ? '#dc2626'
        : pct > 70
          ? '#f59e0b'
          : 'var(--color-accent)'

  return (
    <div className="mb-2 rounded border border-[var(--color-border)] bg-[var(--color-panel)] px-2 py-1.5 text-[10px] text-[var(--color-text-muted)]">
      <div className="mb-1 flex items-center gap-1.5">
        <HardDrive size={11} strokeWidth={1.6} />
        <span className="font-medium text-[var(--color-text)]">Storage</span>
        {persistent ? (
          <span
            className="ml-auto flex items-center gap-1 text-[var(--color-accent)]"
            title="Storage persistente, o browser não vai apagar dados em pressão de espaço"
          >
            <ShieldCheck size={10} strokeWidth={1.6} />
            persistente
          </span>
        ) : (
          <button
            type="button"
            onClick={onAskPersistent}
            disabled={requesting}
            title="Pedir ao browser para marcar este storage como persistente (resiste a eviction quando o disco está cheio)"
            className="ml-auto rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[9px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] disabled:opacity-50"
          >
            {requesting ? 'a pedir…' : 'tornar persistente'}
          </button>
        )}
      </div>
      {pct !== null ? (
        <>
          <div className="h-1 w-full overflow-hidden rounded bg-[var(--color-bg)]">
            <div
              className="h-full transition-[width]"
              style={{
                width: `${pct.toFixed(2)}%`,
                background: barColor,
              }}
            />
          </div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <span title="Total ocupado por esta origem (todos os dados em IndexedDB, localStorage, caches do browser)">
              {formatBytes(usage)} / {formatBytes(quota)} ({pct.toFixed(1)}%)
            </span>
            <span title="Quanto deste total vem dos viewpoints (thumbnails + metadados)">
              vp: {formatBytes(ownBytes)} ({list.length})
            </span>
          </div>
        </>
      ) : (
        <div className="text-[var(--color-text-dim)]">
          Indisponível neste browser. Viewpoints locais: {formatBytes(ownBytes)}{' '}
          ({list.length}).
        </div>
      )}
    </div>
  )
}

function ViewpointCard({
  vp,
  onApply,
  onDelete,
  onRename,
  onUpdate,
  onMove,
}: {
  vp: LocalViewpoint
  onApply: () => void
  onDelete: () => void
  onRename: () => void
  onUpdate: () => void
  onMove: () => void
}) {
  const url = useThumbnailUrl(vp.thumbnail)
  const created = useMemo(() => formatDate(vp.createdAt), [vp.createdAt])
  return (
    <li className="group overflow-hidden rounded border border-[var(--color-border)] bg-[var(--color-panel)] hover:border-[var(--color-accent)]/40">
      <button
        type="button"
        onClick={onApply}
        title="Aplicar este viewpoint"
        className="block w-full"
      >
        <div className="aspect-video w-full bg-[var(--color-bg)]">
          {url ? (
            <img
              src={url}
              alt={vp.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[var(--color-text-dim)]">
              <ImageIcon size={32} strokeWidth={1.2} />
            </div>
          )}
        </div>
        <div className="px-2 py-1.5 text-left">
          <div className="truncate text-xs font-medium text-[var(--color-text)]">
            {vp.name}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[10px] text-[var(--color-text-dim)]">
            <span>{created}</span>
            {vp.state.hidden.length > 0 && (
              <span title="Tem elementos escondidos">
                {vp.state.hidden.reduce((n, h) => n + h.localIds.length, 0)} esc.
              </span>
            )}
            {vp.state.transparent.length > 0 && (
              <span title="Tem elementos com transparência">
                {vp.state.transparent.reduce(
                  (n, h) => n + h.localIds.length,
                  0,
                )}{' '}
                transp.
              </span>
            )}
          </div>
        </div>
      </button>
      <div className="flex items-center justify-end gap-1 border-t border-[var(--color-border-subtle)] bg-[var(--color-bg)]/40 px-1.5 py-1">
        <button
          type="button"
          onClick={onApply}
          title="Aplicar (Enter)"
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
        >
          <Play size={11} strokeWidth={1.6} />
          Aplicar
        </button>
        <button
          type="button"
          onClick={onUpdate}
          title="Atualizar (substituir pela vista atual)"
          aria-label="Atualizar"
          className="flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
        >
          <RefreshCw size={11} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          onClick={onMove}
          title="Mover para pasta"
          aria-label="Mover para pasta"
          className="flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
        >
          <FolderInput size={11} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          onClick={onRename}
          title="Renomear"
          aria-label="Renomear"
          className="flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-muted)] hover:bg-[var(--color-bg)] hover:text-[var(--color-text)]"
        >
          <Pencil size={11} strokeWidth={1.6} />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Apagar"
          aria-label="Apagar"
          className="flex h-6 w-6 items-center justify-center rounded text-[var(--color-text-muted)] hover:border-red-700/50 hover:bg-[var(--color-error-bg)] hover:text-[var(--color-error-text)]"
        >
          <Trash2 size={11} strokeWidth={1.6} />
        </button>
      </div>
    </li>
  )
}

// Wrap a Blob in an object URL with proper cleanup. Re-runs when the
// blob reference changes (e.g. user re-captures and replaces the
// thumbnail in-place).
function useThumbnailUrl(blob: Blob | null): string | null {
  // O endereço deriva-se do blob, em vez de ser escrito em estado por
  // um efeito. Assim a miniatura aparece no MESMO render em que o blob
  // chega, em vez de custar um render extra por miniatura, e não é
  // preciso o ref que servia para saber qual era o endereço corrente.
  //
  // O efeito fica só com o que é mesmo efeito: devolver o endereço ao
  // browser quando deixa de ser preciso. Em desenvolvimento, o modo
  // estrito do React corre o `useMemo` duas vezes e deita fora um dos
  // endereços sem o revogar; é um endereço de miniatura por montagem,
  // só em desenvolvimento, e não vale a complicação de o evitar.
  const url = useMemo(
    () => (blob ? URL.createObjectURL(blob) : null),
    [blob],
  )
  useEffect(() => {
    if (!url) return
    return () => URL.revokeObjectURL(url)
  }, [url])
  return url
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso)
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
  } catch {
    return iso
  }
}

export default ViewpointsPanel
